import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  FileImage,
  FileSpreadsheet,
  FileArchive,
  Presentation,
  File,
  Download,
  FolderOpen,
  Clock,
  ChevronDown,
  Lock,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { documentsApi, uploadsApi, CourseDocument, DocumentCategory } from '../lib/api';
import toast from 'react-hot-toast';

// Document category config
const CATEGORY_CONFIG: Record<DocumentCategory, { label: string; color: string; bgColor: string; icon: React.ElementType }> = {
  SYLLABUS: { label: 'Syllabus', color: 'text-purple-400', bgColor: 'bg-purple-500/20', icon: FileText },
  LECTURE_NOTES: { label: 'Lecture Notes', color: 'text-blue-400', bgColor: 'bg-blue-500/20', icon: Presentation },
  RESOURCE: { label: 'Resources', color: 'text-emerald-400', bgColor: 'bg-emerald-500/20', icon: FolderOpen },
  ASSIGNMENT: { label: 'Assignments', color: 'text-amber-400', bgColor: 'bg-amber-500/20', icon: FileText },
  SOLUTION: { label: 'Solutions', color: 'text-green-400', bgColor: 'bg-green-500/20', icon: FileText },
  TEMPLATE: { label: 'Templates', color: 'text-cyan-400', bgColor: 'bg-cyan-500/20', icon: File },
  OTHER: { label: 'Other', color: 'text-gray-400', bgColor: 'bg-gray-500/20', icon: File },
};

// File type icons
const getFileIcon = (mimeType: string) => {
  if (mimeType.startsWith('image/')) return FileImage;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType === 'text/csv') return FileSpreadsheet;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return Presentation;
  if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('word')) return FileText;
  if (mimeType.includes('zip') || mimeType.includes('archive')) return FileArchive;
  return File;
};

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Format relative time
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
};

interface CourseDocumentsProps {
  courseId: string;
  hasAccess: boolean;
  className?: string;
}

export default function CourseDocuments({ courseId, hasAccess, className = '' }: CourseDocumentsProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState<CourseDocument[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [hasAccessState, setHasAccessState] = useState(hasAccess);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Handle document download (supports private storage URLs)
  const handleDownload = useCallback(async (doc: CourseDocument) => {
    try {
      setDownloadingId(doc.id);
      await uploadsApi.downloadFile(doc.fileUrl, doc.fileName);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download file');
    } finally {
      setDownloadingId(null);
    }
  }, []);

  useEffect(() => {
    const fetchDocuments = async () => {
      setIsLoading(true);
      try {
        const response = await documentsApi.getByCourse(courseId);
        if (response.data.success) {
          setDocuments(response.data.data?.documents || []);
          setHasAccessState(response.data.data?.hasAccess ?? hasAccess);
          // Expand first category by default
          const categories = [...new Set((response.data.data?.documents || []).map(d => d.category))];
          if (categories.length > 0) {
            setExpandedCategories([categories[0]]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch documents:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (courseId) {
      fetchDocuments();
    }
  }, [courseId, hasAccess]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Group documents by category
  const groupedDocs = documents.reduce((acc, doc) => {
    const cat = doc.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {} as Record<DocumentCategory, CourseDocument[]>);

  // Get recent documents (last 5)
  const recentDocs = [...documents]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <div className="w-8 h-8 border-2 border-white/20 border-t-coral rounded-full animate-spin" />
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
          <FolderOpen className="w-8 h-8 text-white/30" />
        </div>
        <p className="text-white/50">No documents available yet</p>
        <p className="text-white/30 text-sm mt-1">
          The instructor will upload course materials here
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Recent Documents Section */}
      {recentDocs.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-coral" />
            <h4 className="font-medium text-white">Recently Added</h4>
          </div>
          <div className="space-y-2">
            {recentDocs.slice(0, 3).map((doc) => {
              const FileIcon = getFileIcon(doc.mimeType);
              const config = CATEGORY_CONFIG[doc.category];
              const canAccess = hasAccessState || doc.isPublic;

              return (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="group relative"
                >
                  {canAccess ? (
                    <button
                      onClick={() => handleDownload(doc)}
                      disabled={downloadingId === doc.id}
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 hover:border-white/10 transition-all text-left disabled:opacity-50"
                    >
                      <div className={`w-10 h-10 rounded-lg ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                        <FileIcon className={`w-5 h-5 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">{doc.title}</p>
                        <div className="flex items-center gap-2 text-xs text-white/40">
                          <Clock className="w-3 h-3" />
                          <span>{formatRelativeTime(doc.createdAt)}</span>
                          <span>•</span>
                          <span>{formatFileSize(doc.fileSize)}</span>
                        </div>
                      </div>
                      {downloadingId === doc.id ? (
                        <Loader2 className="w-4 h-4 text-coral animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
                      )}
                    </button>
                  ) : (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5 opacity-60">
                      <div className={`w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0`}>
                        <Lock className="w-5 h-5 text-white/40" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white/60 font-medium text-sm truncate">{doc.title}</p>
                        <p className="text-xs text-white/30">Enroll to access</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Categorized Documents */}
      <div className="space-y-3">
        {Object.entries(CATEGORY_CONFIG).map(([category, config]) => {
          const categoryDocs = groupedDocs[category as DocumentCategory] || [];
          if (categoryDocs.length === 0) return null;

          const isExpanded = expandedCategories.includes(category);
          const CategoryIcon = config.icon;

          return (
            <div
              key={category}
              className="rounded-xl overflow-hidden border border-white/10 bg-white/[0.02]"
            >
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg ${config.bgColor} flex items-center justify-center`}>
                    <CategoryIcon className={`w-4 h-4 ${config.color}`} />
                  </div>
                  <span className="font-medium text-white text-sm">{config.label}</span>
                  <span className="text-white/40 text-xs">({categoryDocs.length})</span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-white/40 transition-transform ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Documents */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-3 space-y-2">
                      {categoryDocs.map((doc) => {
                        const FileIcon = getFileIcon(doc.mimeType);
                        const canAccess = hasAccessState || doc.isPublic;

                        return (
                          <div key={doc.id} className="group">
                            {canAccess ? (
                              <button
                                onClick={() => handleDownload(doc)}
                                disabled={downloadingId === doc.id}
                                className="w-full flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] transition-colors text-left disabled:opacity-50"
                              >
                                <FileIcon className="w-5 h-5 text-white/50" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-white/90 text-sm truncate">{doc.title}</p>
                                  {doc.description && (
                                    <p className="text-white/40 text-xs truncate">{doc.description}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-white/30 text-xs">
                                  <span>{formatFileSize(doc.fileSize)}</span>
                                  {downloadingId === doc.id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-coral" />
                                  ) : (
                                    <Download className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                  )}
                                </div>
                              </button>
                            ) : (
                              <div className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.01] opacity-50">
                                <Lock className="w-5 h-5 text-white/30" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-white/50 text-sm truncate">{doc.title}</p>
                                  <p className="text-white/30 text-xs">Enroll to access</p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Access Notice */}
      {!hasAccessState && documents.some(d => !d.isPublic) && (
        <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-coral/10 to-purple-500/10 border border-coral/20">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-coral flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-medium text-sm">Enroll to access all documents</p>
              <p className="text-white/50 text-xs mt-1">
                Some documents are only available to enrolled students
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
